import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { sharedStorage } from '../utils/sharedStorage';

const BagContext = createContext(null);
const bagStorage = sharedStorage('fozzil-bag');
const pendingStorage = sharedStorage('fozzil-pending-bag-item');

export const useBag = () => {
  const ctx = useContext(BagContext);
  if (!ctx) throw new Error('useBag must be used within BagProvider');
  return ctx;
};

// Store a pending bag item before auth redirect
export const setPendingBagItem = (item) => {
  pendingStorage.setItem(null, JSON.stringify(item));
};

export const getPendingBagItem = () => {
  try {
    const item = pendingStorage.getItem();
    return item ? JSON.parse(item) : null;
  } catch {
    return null;
  }
};

export const clearPendingBagItem = () => {
  pendingStorage.removeItem();
};

export const BagProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = bagStorage.getItem();
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Persist bag to shared cookie storage
  useEffect(() => {
    bagStorage.setItem(null, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((item) => {
    // item = { id, name, tagline, image, config: [{label, value}], price, quantity }
    setItems(prev => {
      // Check if same config already exists
      const existingIdx = prev.findIndex(i => i.id === item.id && JSON.stringify(i.config) === JSON.stringify(item.config));
      if (existingIdx >= 0) {
        const updated = [...prev];
        updated[existingIdx] = { ...updated[existingIdx], quantity: updated[existingIdx].quantity + 1 };
        return updated;
      }
      return [...prev, { ...item, bagId: Date.now().toString(), quantity: item.quantity || 1 }];
    });
  }, []);

  const removeItem = (bagId) => {
    setItems(prev => prev.filter(i => i.bagId !== bagId));
  };

  const updateQuantity = (bagId, quantity) => {
    if (quantity < 1) return removeItem(bagId);
    setItems(prev => prev.map(i => i.bagId === bagId ? { ...i, quantity } : i));
  };

  const clearBag = () => setItems([]);

  // Process any pending bag item (called after auth completes)
  const processPendingItem = useCallback(() => {
    const pending = getPendingBagItem();
    if (pending) {
      addItem(pending);
      clearPendingBagItem();
      return true; // Item was processed
    }
    return false;
  }, [addItem]);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + (i.price * i.quantity), 0);

  return (
    <BagContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearBag,
      itemCount,
      subtotal,
      processPendingItem,
    }}>
      {children}
    </BagContext.Provider>
  );
};

export default BagContext;
